// src/pages/StaffManagement.jsx
import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const [staffBody, rolesBody] = await Promise.all([api('/staff'), api('/staff/roles')]);
      setStaff(staffBody.staff);
      setRoles(rolesBody.roles);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDeactivate(id) {
    if (!confirm('Deactivate this staff account? They will be signed out immediately.')) return;
    try {
      await api(`/staff/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleReactivate(id) {
    try {
      await api(`/staff/${id}/reactivate`, { method: 'POST' });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRoleChange(id, roleId) {
    try {
      await api(`/staff/${id}`, { method: 'PATCH', body: JSON.stringify({ roleId }) });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-charcoal">Staff</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-ink text-white text-sm font-medium px-4 py-2 hover:bg-ink-700"
        >
          + Add staff
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-p1" role="alert">{error}</p>}

      <div className="mt-6 rounded-lg border border-ink/10 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink/5 text-left text-xs uppercase tracking-wide text-ink/50">
            <tr>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Username</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">MFA</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className={`border-t border-ink/5 ${!s.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-2.5 font-medium">{s.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-ink/60">{s.username}</td>
                <td className="px-4 py-2.5">
                  <select
                    value={roles.find((r) => r.name === s.role_name)?.id ?? ''}
                    onChange={(e) => handleRoleChange(s.id, e.target.value)}
                    disabled={!s.is_active}
                    className="text-xs rounded border border-ink/20 px-1.5 py-1 bg-white"
                  >
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs ${s.mfa_enabled ? 'text-accent' : 'text-ink/40'}`}>
                    {s.mfa_enabled ? 'Enabled' : 'Not set up'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs">{s.is_active ? 'Active' : 'Deactivated'}</td>
                <td className="px-4 py-2.5 text-right">
                  {s.is_active ? (
                    <button onClick={() => handleDeactivate(s.id)} className="text-xs text-p1 hover:underline">Deactivate</button>
                  ) : (
                    <button onClick={() => handleReactivate(s.id)} className="text-xs text-accent hover:underline">Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateStaffModal roles={roles} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function CreateStaffModal({ roles, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', username: '', password: '', roleId: '', specialty: '', email: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/staff', { method: 'POST', body: JSON.stringify(form) });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-6 max-w-sm w-full space-y-3">
        <h2 className="font-semibold text-charcoal">Add staff account</h2>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        <input required placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        <input required type="password" placeholder="Temporary password (10+ chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        <select required value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm">
          <option value="">Select role…</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <input placeholder="Specialty (optional, e.g. counsellor)" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        {error && <p className="text-xs text-p1">{error}</p>}
        <p className="text-xs text-ink/50">They should change this password and enroll MFA on first login.</p>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 text-sm text-ink/60 py-2">Cancel</button>
          <button type="submit" disabled={busy} className="flex-1 rounded-md bg-ink text-white text-sm font-medium py-2 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
